import {
  FormattedDateTime,
  Emphasis,
  Icon,
  Paragraph,
  PlainButton,
  Strong,
  TableTd,
  TableTr,
  Text,
  Toggle,
} from '@customerio/pluma-components/react';
import { optionNeedsSchemaUpgrade, type WarehouseDataOption } from '@/features/warehouse-sync';
import type { WarehouseExportFile, WarehouseSync } from '@customerio/hydra-ui-api';
import * as styles from './details.css';

type DetailsRowProps = {
  sync: WarehouseSync;
  option: WarehouseDataOption;
  isActive: boolean;
  isReadOnly: boolean;
  onToggle: (optionId: WarehouseDataOption['id'], nextValue: boolean) => void;
  onUpgradeSchema: () => void;
};

/** The export file backing this option, if one has been written. */
function latestExportFileFor(sync: WarehouseSync, optionId: string): WarehouseExportFile | undefined {
  return sync.export_files?.find((file) => file.file_name.includes(optionId));
}

export function DetailsRow({ sync, option, isActive, isReadOnly, onToggle, onUpgradeSchema }: DetailsRowProps) {
  const needsUpgrade = optionNeedsSchemaUpgrade(sync.version, option.id);
  const exportFile = needsUpgrade ? undefined : latestExportFileFor(sync, option.id);
  const itemCount = exportFile?.item_count ?? 0;

  return (
    <TableTr data-test-warehouse-detail-row={option.id}>
      <TableTd px="200" py="150" className={needsUpgrade ? styles.disabledCell : undefined}>
        <Strong>{option.name}</Strong>
        <Paragraph variant="secondary" sizeVariant="xs">
          {option.description}
        </Paragraph>
      </TableTd>

      <TableTd px="200" py="150" data-test-warehouse-detail-last-synced>
        {needsUpgrade ? (
          <>
            <PlainButton onClick={onUpgradeSchema} isDisabled={isReadOnly}>
              Upgrade sync
            </PlainButton>{' '}
            to export {option.name} data.
          </>
        ) : exportFile != null ? (
          <>
            <FormattedDateTime date={exportFile.created_at * 1000} format="medium_with_time" withRelativeDateFromNow />
            <Paragraph variant="secondary" sizeVariant="xs">
              {itemCount > 0 ? new Intl.NumberFormat().format(itemCount) : '—'} items exported.
            </Paragraph>
            <Paragraph variant="secondary" sizeVariant="xs">
              Data up to{' '}
              <FormattedDateTime
                date={exportFile.latest_timestamp * 1000}
                format="medium_with_time"
                withRelativeDateFromNow
              />
            </Paragraph>
            <Paragraph variant="secondary" sizeVariant="xs" overflowWrap="anywhere">
              <Icon name="import" color="subtle" size="xs" /> {exportFile.file_name}
            </Paragraph>
          </>
        ) : (
          <Text color="subtle">
            <Emphasis>Not synced recently</Emphasis>
          </Text>
        )}
      </TableTd>

      <TableTd px="200" py="150" textAlign="right">
        <Toggle
          data-test-warehouse-detail-enabled
          ariaLabel={option.name}
          isChecked={isActive}
          onChange={() => onToggle(option.id, !isActive)}
          isDisabled={isReadOnly || needsUpgrade}
          size="sm"
        />
      </TableTd>
    </TableTr>
  );
}
